/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import type { ComponentProps } from 'react';
import {
    Root,
    Trigger,
    Portal,
    Close,
    Overlay,
    Content,
    Title,
    Description,
    type DialogProps as RadixDialogProps,
    type DialogTriggerProps,
    type DialogPortalProps,
    type DialogCloseProps,
    type DialogOverlayProps,
    type DialogContentProps as RadixDialogContentProps,
    type DialogTitleProps,
    type DialogDescriptionProps,
} from '@radix-ui/react-dialog';
import { XIcon } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const dialogContentVariants = cva(
    'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-1/2 left-1/2 z-50 grid w-full translate-x-[-50%] translate-y-[-50%] gap-4 border shadow-lg duration-200 outline-none',
    {
        variants: {
            mode: {
                auto: 'max-w-[calc(100%-2rem)] rounded-lg p-6 sm:max-w-lg',
                default: 'max-w-[calc(100%-2rem)] rounded-lg p-6 sm:max-w-lg',
                compact: 'max-w-[calc(100%-2rem)] rounded-lg p-4 sm:max-w-md',
                wide: 'max-w-[calc(100%-2rem)] rounded-lg p-6 sm:max-w-3xl',
                fullscreen:
                    'h-[100dvh] w-[100dvw] max-w-none translate-x-[-50%] translate-y-[-50%] rounded-none border-0 p-6',
                dark: 'max-w-[calc(100%-2rem)] rounded-lg p-0 sm:max-w-lg bg-foreground  text-primary-foreground  overflow-hidden',
            },
        },
        defaultVariants: {
            mode: 'auto',
        },
    }
);

function Dialog(props: RadixDialogProps) {
    return <Root data-slot="dialog" {...props} />;
}

function DialogTrigger(props: DialogTriggerProps) {
    return <Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal(props: DialogPortalProps) {
    return <Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose(props: DialogCloseProps) {
    return <Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: DialogOverlayProps) {
    return (
        <Overlay
            data-slot="dialog-overlay"
            className={cn(
                'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-foreground/50',
                className
            )}
            {...props}
        />
    );
}

type DialogContentProps = RadixDialogContentProps &
    VariantProps<typeof dialogContentVariants> & {
        showCloseButton?: boolean;
    };

function DialogContent({ className, children, mode = 'auto', showCloseButton = true, ...props }: DialogContentProps) {
    return (
        <DialogPortal>
            <DialogOverlay className={mode === 'dark' ? 'bg-foreground/85 backdrop-blur-xl' : undefined} />
            <Content
                data-slot="dialog-content"
                data-mode={mode}
                className={cn(
                    dialogContentVariants({ mode }),
                    mode === 'dark' &&
                        '[&>[data-slot=dialog-header]]:gap-0 [&>[data-slot=dialog-header]]:border-b [&>[data-slot=dialog-header]]:border-muted-foreground [&>[data-slot=dialog-header]]:px-6 [&>[data-slot=dialog-header]]:py-4 [&>[data-slot=dialog-header]_[data-slot=dialog-title]]:text-primary-foreground [&>[data-slot=dialog-header]_[data-slot=dialog-title]]:text-lg [&>[data-slot=dialog-header]_[data-slot=dialog-title]]:leading-[1.2] [&>[data-slot=dialog-header]_[data-slot=dialog-title]]:font-semibold [&>[data-slot=dialog-description]]:text-primary-foreground/80 [&>[data-slot=dialog-header]+*]:px-6 [&>[data-slot=dialog-header]+*]:py-8 [&>[data-slot=dialog-footer]]:px-6 [&>[data-slot=dialog-footer]]:pb-6 [&>[data-slot=dialog-footer]]:pt-0',
                    className
                )}
                {...props}>
                {children}
                {showCloseButton ? (
                    <Close
                        data-slot="dialog-close"
                        className={cn(
                            'ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4',
                            mode === 'dark' && 'top-4 right-6 text-primary-foreground'
                        )}>
                        <XIcon />
                        <span className="sr-only">Close</span>
                    </Close>
                ) : null}
            </Content>
        </DialogPortal>
    );
}

function DialogHeader({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="dialog-header"
            className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
            {...props}
        />
    );
}

function DialogFooter({ className, ...props }: ComponentProps<'div'>) {
    return (
        <div
            data-slot="dialog-footer"
            className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end p-6  pt-0', className)}
            {...props}
        />
    );
}

function DialogTitle({ className, ...props }: DialogTitleProps) {
    return (
        <Title data-slot="dialog-title" className={cn('text-lg leading-tight font-semibold', className)} {...props} />
    );
}

function DialogDescription({ className, ...props }: DialogDescriptionProps) {
    return (
        <Description
            data-slot="dialog-description"
            className={cn('text-muted-foreground text-sm', className)}
            {...props}
        />
    );
}

export {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogTitle,
    DialogTrigger,
};
